import { useEffect, useState } from "react";
import { FileText, Download, X, Users, Calendar } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

function fmt(v) {
  return Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
}
function shortDate(s) { return (s || "").slice(0, 10); }
function statusPill(s) {
  const map = {
    credited: { c: "#a7f3d0", b: "rgba(34,192,115,0.4)", g: "rgba(34,192,115,0.12)" },
    paid:     { c: "#fde68a", b: "rgba(212,175,55,0.4)", g: "rgba(212,175,55,0.12)" },
    skipped:  { c: "#cbd5e1", b: "rgba(148,163,184,0.3)", g: "rgba(148,163,184,0.10)" },
    below_threshold: { c: "#fca5a5", b: "rgba(229,75,89,0.3)", g: "rgba(229,75,89,0.10)" },
  };
  return map[s] || map.credited;
}

export default function Settlements() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  // Drill-down
  const [active, setActive] = useState(null);
  const [players, setPlayers] = useState([]);
  const [pLoading, setPLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/aff-panel/settlements${filter !== "all" ? `?status=${filter}` : ""}`);
      setRows(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load settlements");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = async (s) => {
    setActive(s);
    setPlayers([]);
    setPLoading(true);
    try {
      const r = await api.get(`/aff-panel/settlements/${s.id}/players`);
      setPlayers(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load players");
    } finally { setPLoading(false); }
  };

  const downloadPdf = async (id) => {
    try {
      const token = localStorage.getItem("ss_promo_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/aff-panel/settlements/${id}.pdf`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("PDF download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `settlement_${id.slice(0, 8)}.pdf`;
      a.click();
    } catch (e) { toast.error("Could not download PDF"); }
  };

  return (
    <div data-testid="settlements-page">
      <PageHeader
        eyebrow="EARNINGS"
        title="Commission Settlements"
        subtitle="Frozen 15-day snapshots — every settled commission with full player-level breakdown."
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {["all", "credited", "paid", "skipped", "below_threshold"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className="px-3 h-9 rounded-lg text-[11.5px] font-bold transition"
            style={{
              background: filter === k ? "rgba(34, 192, 115, 0.18)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${filter === k ? "rgba(34, 192, 115, 0.45)" : "rgba(255,255,255,0.08)"}`,
              color: filter === k ? "#a7f3d0" : "#cbd5e1",
            }}
            data-testid={`filter-${k}`}
          >
            {k.replace("_", " ")} ({counts[k] || 0})
          </button>
        ))}
      </div>

      <div className="p-glass overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr style={{ background: "rgba(255,255,255,0.04)", color: "#a7f3d0" }}>
              <th className="text-left p-3">Period</th>
              <th className="text-right p-3">Players</th>
              <th className="text-right p-3">Deposits</th>
              <th className="text-right p-3">Withdraws</th>
              <th className="text-right p-3">Net Loss</th>
              <th className="text-right p-3">Rate</th>
              <th className="text-right p-3">Commission</th>
              <th className="text-center p-3">Status</th>
              <th className="text-center p-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="p-8 text-center text-white/55">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={9} className="p-8 text-center text-white/55">No settlements yet — wait for the next 15-day cycle to close.</td></tr>
            ) : rows.map((r) => {
              const sp = statusPill(r.status);
              return (
                <tr key={r.id} className="border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                  <td className="p-3 text-white">
                    <div className="flex items-center gap-1.5"><Calendar size={11} className="text-white/40" />
                      {shortDate(r.period_start)} → {shortDate(r.period_end)}
                    </div>
                  </td>
                  <td className="p-3 text-right text-white">{r.total_players || 0}</td>
                  <td className="p-3 text-right text-white/85">৳{fmt(r.total_deposits)}</td>
                  <td className="p-3 text-right text-white/85">৳{fmt(r.total_withdrawals)}</td>
                  <td className="p-3 text-right text-white font-bold">৳{fmt(r.net_loss)}</td>
                  <td className="p-3 text-right text-emerald-300 font-bold">{r.commission_rate}%</td>
                  <td className="p-3 text-right" style={{ color: "#d4af37", fontWeight: 700 }}>৳{fmt(r.commission_amount)}</td>
                  <td className="p-3 text-center">
                    <span className="text-[9.5px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-md"
                      style={{ color: sp.c, background: sp.g, border: `1px solid ${sp.b}` }}>
                      {r.status}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openDetail(r)} className="p-btn-ghost px-2 h-8 rounded-md text-[11px] inline-flex items-center gap-1" data-testid={`view-${r.id}`}>
                        <Users size={11} /> Players
                      </button>
                      <button onClick={() => downloadPdf(r.id)} className="p-btn-gold px-2 h-8 rounded-md text-[11px] inline-flex items-center gap-1" data-testid={`pdf-${r.id}`}>
                        <Download size={11} /> PDF
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="p-modal-overlay" onClick={() => setActive(null)}>
          <div className="p-modal" style={{ maxWidth: 820 }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-200 font-bold">SETTLEMENT BREAKDOWN</div>
                <div className="text-white font-black text-lg mt-1">{shortDate(active.period_start)} → {shortDate(active.period_end)}</div>
                <div className="text-white/55 text-[12px]">ID: {active.id.slice(0, 8)} · {active.total_players || 0} players</div>
              </div>
              <button onClick={() => setActive(null)} className="p-btn-ghost p-2 rounded-lg"><X size={14} /></button>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3 text-[11.5px]">
              <div className="p-glass-light p-3">
                <div className="uppercase tracking-[0.18em] text-white/45 text-[9.5px] font-bold">Net Loss</div>
                <div className="text-white font-black text-[14px] mt-0.5">৳{fmt(active.net_loss)}</div>
              </div>
              <div className="p-glass-light p-3">
                <div className="uppercase tracking-[0.18em] text-white/45 text-[9.5px] font-bold">Rate</div>
                <div className="text-emerald-300 font-black text-[14px] mt-0.5">{active.commission_rate}%</div>
              </div>
              <div className="p-glass-light p-3">
                <div className="uppercase tracking-[0.18em] text-white/45 text-[9.5px] font-bold">Commission</div>
                <div className="font-black text-[14px] mt-0.5" style={{ color: "#d4af37" }}>৳{fmt(active.commission_amount)}</div>
              </div>
            </div>
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
              <table className="w-full text-[12px]">
                <thead><tr style={{ background: "rgba(255,255,255,0.04)", color: "#a7f3d0" }}>
                  <th className="text-left p-2.5">Username</th>
                  <th className="text-right p-2.5">Deposit</th>
                  <th className="text-right p-2.5">Withdraw</th>
                  <th className="text-right p-2.5">Net Loss</th>
                  <th className="text-right p-2.5">Commission</th>
                </tr></thead>
                <tbody>
                  {pLoading ? (
                    <tr><td colSpan={5} className="p-6 text-center text-white/55">Loading players…</td></tr>
                  ) : players.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-white/55">No player activity in this period.</td></tr>
                  ) : players.map((p, i) => (
                    <tr key={i} className="border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                      <td className="p-2.5 text-white">{p.username || "—"}</td>
                      <td className="p-2.5 text-right text-white/85">৳{fmt(p.deposit)}</td>
                      <td className="p-2.5 text-right text-white/85">৳{fmt(p.withdraw)}</td>
                      <td className="p-2.5 text-right font-bold" style={{ color: p.net_loss >= 0 ? "#a7f3d0" : "#fca5a5" }}>৳{fmt(p.net_loss)}</td>
                      <td className="p-2.5 text-right font-bold" style={{ color: "#d4af37" }}>৳{fmt(p.commission_contribution)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <button onClick={() => downloadPdf(active.id)} className="p-btn-gold mt-4 w-full h-11 rounded-xl text-[13px] font-bold inline-flex items-center justify-center gap-2" data-testid="download-pdf-detail">
              <Download size={14} /> Download Full PDF
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
